import { useEffect, useState } from "react";
import {
    FiCreditCard,
    FiLoader,
    FiCheckCircle,
    FiXCircle,
    FiDollarSign,
    FiZap,
} from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchMyOrders } from "../../store/smileDesign/paymentSlice";
import TablePagination from "../../components/smileDesign/TablePagination";
import { CREDIT_SERVICE } from "../../constants/creditService";
import { getCreditUi } from "../../constants/creditPageTheme";

const darkStatus: Record<
    string,
    { label: string; className: string; icon: React.ReactNode }
> = {
    PAID: {
        label: "Paid",
        className: "bg-green-500/10 border-green-500/20 text-green-400",
        icon: <FiCheckCircle />,
    },
    FAILED: {
        label: "Failed",
        className: "bg-red-500/10 border-red-500/20 text-red-400",
        icon: <FiXCircle />,
    },
    REFUNDED: {
        label: "Refunded",
        className: "bg-purple-500/10 border-purple-500/20 text-purple-400",
        icon: <FiCreditCard />,
    },
};

const lightStatus: Record<
    string,
    { label: string; className: string; icon: React.ReactNode }
> = {
    PAID: {
        label: "Paid",
        className: "bg-emerald-50 border-emerald-200 text-emerald-700",
        icon: <FiCheckCircle />,
    },
    FAILED: {
        label: "Failed",
        className: "bg-red-50 border-red-200 text-red-700",
        icon: <FiXCircle />,
    },
    REFUNDED: {
        label: "Refunded",
        className: "bg-purple-50 border-purple-200 text-purple-700",
        icon: <FiCreditCard />,
    },
};

const formatDate = (value: string) =>
    new Date(value).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

const MyOrders = ({
    service = CREDIT_SERVICE.SMILE,
}: {
    service?: string;
}) => {
    const dispatch = useAppDispatch();
    const { myOrders, myOrdersPagination, myOrdersStats, loading } =
        useAppSelector((state) => state.payment);
    const { user } = useAppSelector((state) => state.auth);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState("");

    const isPhotoRecolour = service === CREDIT_SERVICE.PHOTO_RECOLOUR;
    const t = getCreditUi(isPhotoRecolour ? "light" : "dark");
    const statusConfig = isPhotoRecolour ? lightStatus : darkStatus;
    const creditBalance = isPhotoRecolour
        ? (user?.photo_recolour_credits ?? 0)
        : (user?.smile_credits ?? 0);

    const loadOrders = (overrides?: { page?: number; status?: string }) => {
        dispatch(
            fetchMyOrders({
                page: overrides?.page ?? page,
                service,
                status: (overrides?.status ?? statusFilter) || undefined,
            })
        );
    };

    useEffect(() => {
        loadOrders({ page: 1 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [service]);

    const handleStatusFilter = (value: string) => {
        setStatusFilter(value);
        setPage(1);
        loadOrders({ page: 1, status: value });
    };

    const currency = myOrdersStats?.currency || "";

    return (
        <div className={t.page}>
            <div className="w-full">
                <div className="mb-8">
                    <h1 className={t.heading}>Order History</h1>
                    <p className={t.subtitle}>
                        See when you bought credits, how much you spent, and how
                        many credits you received.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
                    <div className={`${t.card} p-5`}>
                        <p className={`${t.muted} text-sm`}>Total spent</p>
                        <h2 className={`text-3xl font-bold ${t.success} mt-2`}>
                            {currency}
                            {(myOrdersStats?.totalSpent ?? 0).toFixed(2)}
                        </h2>
                    </div>
                    <div className={`${t.card} p-5`}>
                        <p className={`${t.muted} text-sm`}>Credits purchased</p>
                        <h2 className={`text-3xl font-bold ${t.accent} mt-2`}>
                            {myOrdersStats?.totalCreditsPurchased ?? 0}
                        </h2>
                    </div>
                    <div className={`${t.card} p-5`}>
                        <p className={`${t.muted} text-sm`}>Paid orders</p>
                        <h2 className={`text-3xl font-bold ${t.warning} mt-2`}>
                            {myOrdersStats?.paidOrders ?? 0}
                        </h2>
                    </div>
                    <div className={`${t.card} p-5`}>
                        <p className={`${t.muted} text-sm`}>Current balance</p>
                        <h2 className={`text-3xl font-bold ${t.body} mt-2`}>
                            {creditBalance}
                        </h2>
                    </div>
                </div>

                <div className="mb-6">
                    <select
                        value={statusFilter}
                        onChange={(e) => handleStatusFilter(e.target.value)}
                        className={`${t.select} w-full sm:w-auto sm:min-w-[180px]`}
                    >
                        <option value="">All statuses</option>
                        <option value="PAID">Paid</option>
                        <option value="FAILED">Failed</option>
                        <option value="REFUNDED">Refunded</option>
                    </select>
                </div>

                {loading && (
                    <div className="flex justify-center py-20">
                        <FiLoader className={`${t.loader} text-4xl`} />
                    </div>
                )}

                {!loading && myOrders.length === 0 && (
                    <div className={`text-center py-20 ${t.card}`}>
                        <FiCreditCard className={`text-5xl ${t.emptyIcon} mx-auto mb-4`} />
                        <h3 className={`text-xl font-semibold mb-2 ${t.body}`}>
                            No orders yet
                        </h3>
                        <p className={t.muted}>
                            Purchase a credit package to see it here.
                        </p>
                    </div>
                )}

                {!loading && myOrders.length > 0 && (
                    <>
                        <div className={`hidden lg:block ${t.tableWrap}`}>
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[880px]">
                                    <thead className={t.tableHead}>
                                        <tr>
                                            <th className={t.tableHeadCell}>When</th>
                                            <th className={t.tableHeadCell}>Package</th>
                                            <th className={t.tableHeadCell}>Amount spent</th>
                                            <th className={t.tableHeadCell}>Credits bought</th>
                                            <th className={t.tableHeadCell}>Status</th>
                                            <th className={t.tableHeadCell}>Order</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {myOrders.map((order) => {
                                            const status =
                                                statusConfig[order.status] ??
                                                statusConfig.PAID;
                                            const totalCredits =
                                                order.total_credits ??
                                                order.credits +
                                                    order.bonus_credits;
                                            return (
                                                <tr
                                                    key={order.order_id}
                                                    className={t.tableRow}
                                                >
                                                    <td className={`px-6 py-5 text-sm ${t.muted} whitespace-nowrap`}>
                                                        {formatDate(order.created_at)}
                                                    </td>
                                                    <td className="px-6 py-5">
                                                        <p className={`font-semibold ${t.body}`}>
                                                            {order.package_name}
                                                        </p>
                                                        {order.bonus_credits > 0 && (
                                                            <p className={`text-xs ${t.muted} mt-1`}>
                                                                {order.credits} + {order.bonus_credits} bonus
                                                            </p>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-5">
                                                        <span className={`inline-flex items-center gap-1.5 ${t.success} font-bold text-lg`}>
                                                            <FiDollarSign />
                                                            {order.currency}
                                                            {order.amount}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-5">
                                                        <span className={`inline-flex items-center gap-1.5 ${t.accent} font-semibold`}>
                                                            <FiZap />
                                                            {totalCredits}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-5">
                                                        <span
                                                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${status.className}`}
                                                        >
                                                            {status.icon}
                                                            {status.label}
                                                        </span>
                                                    </td>
                                                    <td className={`px-6 py-5 ${t.accent} font-medium`}>
                                                        #{order.order_id}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <TablePagination
                                pagination={myOrdersPagination}
                                onPageChange={(nextPage) => {
                                    setPage(nextPage);
                                    loadOrders({ page: nextPage });
                                }}
                                itemLabel="orders"
                                variant={t.pagination}
                            />
                        </div>

                        <div className="lg:hidden space-y-4">
                            {myOrders.map((order) => {
                                const status =
                                    statusConfig[order.status] ??
                                    statusConfig.PAID;
                                const totalCredits =
                                    order.total_credits ??
                                    order.credits + order.bonus_credits;
                                return (
                                    <div
                                        key={order.order_id}
                                        className={`${t.card} p-5`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <p className={`${t.accent} font-medium`}>
                                                    Order #{order.order_id}
                                                </p>
                                                <h3 className={`text-lg font-semibold mt-1 ${t.body}`}>
                                                    {order.package_name}
                                                </h3>
                                            </div>
                                            <span
                                                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${status.className}`}
                                            >
                                                {status.icon}
                                                {status.label}
                                            </span>
                                        </div>
                                        <p className={`${t.muted} text-sm mt-2`}>
                                            {formatDate(order.created_at)}
                                        </p>
                                        <div className="mt-4 space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className={t.muted}>
                                                    Amount spent
                                                </span>
                                                <span className={`${t.success} font-bold`}>
                                                    {order.currency}
                                                    {order.amount}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className={t.muted}>
                                                    Credits bought
                                                </span>
                                                <span className={`${t.accent} font-semibold`}>
                                                    {totalCredits}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div className={t.card}>
                                <TablePagination
                                    pagination={myOrdersPagination}
                                    onPageChange={(nextPage) => {
                                        setPage(nextPage);
                                        loadOrders({ page: nextPage });
                                    }}
                                    itemLabel="orders"
                                    variant={t.pagination}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MyOrders;
